/**
 * @file router.tsx
 * @description Корневой router @chaiberi/admin (phase04 шаги 3 + 5).
 *
 * Использует createBrowserRouter из react-router 7 (library mode). Базовый
 * путь приложения — '/admin/' (см. vite.config.ts `base`), basename для
 * BrowserRouter — '/admin'.
 *
 * Дерево:
 *   /login                     — публично (LoginPage)
 *   /reset/request             — публично (ResetRequestPage)
 *   /reset/:token              — публично (ResetConfirmPage)
 *   /                          — приватно, под RequireAuth:
 *     /select-context          — выбор контекста
 *     /no-context              — нет ни одного контекста
 *     /403                     — нет доступа (ForbiddenPage)
 *     /                        — RootIndex (редирект на /platform или /shop/:slug)
 *     /platform                — RequireContextType=PLATFORM → PlatformLayout
 *       index                  — PlatformDashboard
 *       shops[/new|/:shopId/{overview|domains|integrations|access}] — магазины платформы (phase19.28, PLATFORM_MANAGE_SHOPS)
 *       consent-texts[/new]    — тексты согласия на cookie (Р3г, PLATFORM_MANAGE_PERSONAL_DATA)
 *       integrations           — RequirePermission PLATFORM_MANAGE_INTEGRATIONS
 *       users                  — RequirePermission PLATFORM_MANAGE_USERS
 *     /shop/:slug              — RequireContextType=SHOP (кабинет вкладки по slug) → ShopLayout
 *       index                  — ShopDashboard
 *       catalog                — RequirePermission SHOP_MANAGE_CATALOG
 *       users                  — RequirePermission SHOP_MANAGE_USERS
 *       site/*                 — конструктор сайта (router-site-view.tsx, phase19.16 Р1-6а)
 *       site/seo               — «Сайт → SEO» (Р3в, SB145): контент или настройки
 */

/* eslint-disable react-refresh/only-export-components --
 * router-файл по природе экспортирует и роутер (object), и мини-компоненты
 * редиректа (RootIndex, NotFound). Это однострочные служебные компоненты,
 * выносить в отдельный файл — overkill.
 */
import { Navigate, createBrowserRouter } from "react-router";

import { landingPathFor } from "./auth/format";
import { useAuth } from "./auth/hooks";
import { RequireAuth } from "./components/guards/RequireAuth";
import { RequireContextType } from "./components/guards/RequireContextType";
import { RequirePermission } from "./components/guards/RequirePermission";
import { ConsentTextsAccess } from "./components/platform/consent-texts/ConsentTextsAccess";
import { PlatformLayout } from "./layouts/PlatformLayout";
import { settingsHubRoute } from "./router-settings-hub";
import { siteSeoRoute, siteViewRoute } from "./router-site-view";
import { LegacySiteRedirect } from "./components/site-builder/LegacySiteRedirect";
import { ShopLayout } from "./layouts/ShopLayout";
import { ForbiddenPage } from "./pages/ForbiddenPage";
import { LoginPage } from "./pages/auth/LoginPage";
import { ResetConfirmPage } from "./pages/auth/ResetConfirmPage";
import { ResetRequestPage } from "./pages/auth/ResetRequestPage";
import { NoContextPage } from "./pages/NoContextPage";
import { SelectContextPage } from "./pages/SelectContextPage";
import { BackupsPage } from "./pages/platform/BackupsPage";
import { ConsentTextsEditPage } from "./pages/platform/ConsentTextsEditPage";
import { ConsentTextsPage } from "./pages/platform/ConsentTextsPage";
import { IntegrationsPage } from "./pages/platform/IntegrationsPage";
import { PlatformDashboard } from "./pages/platform/PlatformDashboard";
import { PlatformUsersPage } from "./pages/platform/PlatformUsersPage";
import { ShopsAccess } from "./components/platform/shops/ShopsAccess";
import { ShopAccessTab } from "./pages/platform/shops/ShopAccessTab";
import { ShopCardLayout } from "./pages/platform/shops/ShopCardLayout";
import { ShopCreatePage } from "./pages/platform/shops/ShopCreatePage";
import { ShopDomainsTab } from "./pages/platform/shops/ShopDomainsTab";
import { ShopIntegrationsTab } from "./pages/platform/shops/ShopIntegrationsTab";
import { ShopOverviewTab } from "./pages/platform/shops/ShopOverviewTab";
import { ShopsPage } from "./pages/platform/shops/ShopsPage";
import { CategoryTreePage } from "./pages/shop/catalog/CategoryTreePage";
import { ProducerDetailPage } from "./pages/shop/catalog/ProducerDetailPage";
import { ProducersPage } from "./pages/shop/catalog/ProducersPage";
import { ProductEditPage } from "./pages/shop/catalog/ProductEditPage";
import { ProductKindsPage } from "./pages/shop/catalog/ProductKindsPage";
import { PromotionsPage } from "./pages/shop/catalog/PromotionsPage";
import { ContentArticleEditPage } from "./pages/shop/content/ContentArticleEditPage";
import { ContentArticlesListPage } from "./pages/shop/content/ContentArticlesListPage";
import { ContentAuthorsPage } from "./pages/shop/content/ContentAuthorsPage";
import { ProductReviewsPage } from "./pages/shop/site/ProductReviewsPage";
import { SiteTestimonialsPage } from "./pages/shop/site/SiteTestimonialsPage";
import { ContentCategoriesPage } from "./pages/shop/content/ContentCategoriesPage";
import { ContentTagsPage } from "./pages/shop/content/ContentTagsPage";
import { ProductsListPage } from "./pages/shop/catalog/ProductsListPage";
import { SuppliersPage } from "./pages/shop/catalog/SuppliersPage";
import { TagsPage } from "./pages/shop/catalog/TagsPage";
import { ActivitiesPage } from "./pages/shop/crm/activities/ActivitiesPage";
import { DealDetailPage } from "./pages/shop/crm/deals/DealDetailPage";
import { DealsListPage } from "./pages/shop/crm/deals/DealsListPage";
import { DocumentDetailPage } from "./pages/shop/crm/documents/DocumentDetailPage";
import { DocumentsListPage } from "./pages/shop/crm/documents/DocumentsListPage";
import { KanbanBoardPage } from "./pages/shop/crm/KanbanBoardPage";
import { CustomerDetailPage } from "./pages/shop/customers/CustomerDetailPage";
import { CustomersListPage } from "./pages/shop/customers/CustomersListPage";
import { MessagesRedirect } from "./pages/shop/messages/MessagesRedirect";
import { WmsBinsPage } from "./pages/shop/wms/WmsBinsPage";
import { WmsChannelReservesPage } from "./pages/shop/wms/WmsChannelReservesPage";
import { WmsDashboardPage } from "./pages/shop/wms/WmsDashboardPage";
import { WmsExpiringPage } from "./pages/shop/wms/WmsExpiringPage";
import { WmsPickingPage } from "./pages/shop/wms/WmsPickingPage";
import { WmsPickingTaskPage } from "./pages/shop/wms/WmsPickingTaskPage";
import { WmsReceiptEditPage } from "./pages/shop/wms/WmsReceiptEditPage";
import { WmsReceiptsListPage } from "./pages/shop/wms/WmsReceiptsListPage";
import { WmsSettingsPage } from "./pages/shop/wms/WmsSettingsPage";
import { WmsStockPage } from "./pages/shop/wms/WmsStockPage";
import { WmsTransferEditPage } from "./pages/shop/wms/WmsTransferEditPage";
import { WmsTransfersPage } from "./pages/shop/wms/WmsTransfersPage";
import { WmsWarehousesPage } from "./pages/shop/wms/WmsWarehousesPage";
import { WmsWriteoffEditPage } from "./pages/shop/wms/WmsWriteoffEditPage";
import { WmsWriteoffsPage } from "./pages/shop/wms/WmsWriteoffsPage";
import { AvitoCabinetLayout } from "./pages/shop/avito/AvitoCabinetLayout";
import { AvitoDashboardPage } from "./pages/shop/avito/AvitoDashboardPage";
import { AvitoFinancePage } from "./pages/shop/avito/AvitoFinancePage";
import { AvitoListingDetailPage } from "./pages/shop/avito/AvitoListingDetailPage";
import { AvitoListingsPage } from "./pages/shop/avito/AvitoListingsPage";
import { AvitoOrdersPage } from "./pages/shop/avito/AvitoOrdersPage";
import { AvitoReviewsPage } from "./pages/shop/avito/AvitoReviewsPage";
import { AvitoSyncLogPage } from "./pages/shop/avito/AvitoSyncLogPage";
import { ShopCarrierSettingsPage } from "./pages/shop/carriers/ShopCarrierSettingsPage";
import { ShopCarriersPage } from "./pages/shop/carriers/ShopCarriersPage";
import { ShopPickupPointsPage } from "./pages/shop/carriers/ShopPickupPointsPage";
import { OrderCreatePage } from "./pages/shop/orders/OrderCreatePage";
import { OrdersAttentionPage } from "./pages/shop/orders/OrdersAttentionPage";
import { OrdersBoardPage } from "./pages/shop/orders/OrdersBoardPage";
import { OrderDetailPage as OrderDetailPageOrders } from "./pages/shop/orders/OrderDetailPage";
import { OrdersListPage } from "./pages/shop/orders/OrdersListPage";
import { NotificationLogPage } from "./pages/shop/settings/NotificationLogPage";
import { ShopDashboard } from "./pages/shop/ShopDashboard";

/** Корневой redirect по активному контексту (или select-context, или no-context). */
function RootIndex() {
  const { me } = useAuth();
  if (!me) return <Navigate to="/login" replace />;
  if (me.activeContext) return <Navigate to={landingPathFor(me.activeContext)} replace />;
  if (me.contexts.length === 0) return <Navigate to="/no-context" replace />;
  return <Navigate to="/select-context" replace />;
}

/** 404 — простая страница без хедера; редиректим в /. */
function NotFound() {
  return <Navigate to="/" replace />;
}

export const router = createBrowserRouter(
  [
    { path: "/login", element: <LoginPage /> },
    { path: "/reset/request", element: <ResetRequestPage /> },
    { path: "/reset/:token", element: <ResetConfirmPage /> },
    {
      element: <RequireAuth />,
      children: [
        { path: "/select-context", element: <SelectContextPage /> },
        { path: "/no-context", element: <NoContextPage /> },
        { path: "/403", element: <ForbiddenPage /> },
        { index: true, element: <RootIndex /> },
        {
          path: "platform",
          element: <RequireContextType type="PLATFORM" />,
          children: [
            {
              element: <PlatformLayout />,
              children: [
                { index: true, element: <PlatformDashboard /> },
                {
                  // phase19.28 этап А: магазины платформы; без права — текст «нет доступа» (uiux §7).
                  path: "shops",
                  element: <ShopsAccess />,
                  children: [
                    { index: true, element: <ShopsPage /> },
                    { path: "new", element: <ShopCreatePage /> },
                    {
                      path: ":shopId",
                      element: <ShopCardLayout />,
                      children: [
                        { index: true, element: <Navigate to="overview" replace /> },
                        { path: "overview", element: <ShopOverviewTab /> },
                        { path: "domains", element: <ShopDomainsTab /> },
                        {
                          element: (
                            <ShopsAccess
                              permission="PLATFORM_MANAGE_INTEGRATIONS"
                              title="Нет доступа к интеграциям"
                              description="Интеграции магазина видит администратор платформы с правом «Управление интеграциями»."
                            />
                          ),
                          children: [{ path: "integrations", element: <ShopIntegrationsTab /> }],
                        },
                        {
                          element: (
                            <ShopsAccess
                              permission="PLATFORM_MANAGE_USERS"
                              title="Нет доступа к пользователям"
                              description="Владельца и вход в кабинет магазина видит администратор платформы с правом «Управление пользователями»."
                            />
                          ),
                          children: [{ path: "access", element: <ShopAccessTab /> }],
                        },
                      ],
                    },
                  ],
                },
                {
                  path: "integrations",
                  element: <RequirePermission permissions={["PLATFORM_MANAGE_INTEGRATIONS"]} />,
                  children: [{ index: true, element: <IntegrationsPage /> }],
                },
                {
                  path: "users",
                  element: <RequirePermission permissions={["PLATFORM_MANAGE_USERS"]} />,
                  children: [{ index: true, element: <PlatformUsersPage /> }],
                },
                {
                  // phase19.16 Р3г: тексты согласия на cookie; без права — текст «нужно право» (uiux §5).
                  path: "consent-texts",
                  element: <ConsentTextsAccess />,
                  children: [
                    { index: true, element: <ConsentTextsPage /> },
                    { path: "new", element: <ConsentTextsEditPage /> },
                  ],
                },
                {
                  path: "backups",
                  element: <RequirePermission permissions={["PLATFORM_MANAGE_BACKUPS"]} />,
                  children: [{ index: true, element: <BackupsPage /> }],
                },
              ],
            },
          ],
        },
        {
          path: "shop/:slug",
          element: <RequireContextType type="SHOP" />,
          children: [
            {
              element: <ShopLayout />,
              children: [
                { index: true, element: <ShopDashboard /> },
                {
                  path: "catalog",
                  element: <RequirePermission permissions={["SHOP_MANAGE_CATALOG"]} />,
                  children: [
                    { index: true, element: <ProductsListPage /> },
                    { path: "products", element: <ProductsListPage /> },
                    { path: "products/:id", element: <ProductEditPage /> },
                    { path: "categories", element: <CategoryTreePage /> },
                    { path: "product-kinds", element: <ProductKindsPage /> },
                    { path: "tags", element: <TagsPage /> },
                    { path: "producers", element: <ProducersPage /> },
                    { path: "producers/:id", element: <ProducerDetailPage /> },
                    { path: "suppliers", element: <SuppliersPage /> },
                    { path: "promotions", element: <PromotionsPage /> },
                  ],
                },
                {
                  // phase12.0 — Content cabinet (управление блогом).
                  path: "content",
                  element: <RequirePermission permissions={["SHOP_MANAGE_CONTENT"]} />,
                  children: [
                    { index: true, element: <ContentArticlesListPage /> },
                    { path: "articles", element: <ContentArticlesListPage /> },
                    { path: "articles/new", element: <ContentArticleEditPage /> },
                    { path: "articles/:id", element: <ContentArticleEditPage /> },
                    { path: "categories", element: <ContentCategoriesPage /> },
                    { path: "tags", element: <ContentTagsPage /> },
                    { path: "authors", element: <ContentAuthorsPage /> },
                    // phase19.8 — Page-builder (конструктор страниц сайта).
                    // phase19.16 Р1-6а — страницы сайта правятся в «Сайт → Конструктор сайта».
                    { path: "pages", element: <LegacySiteRedirect /> },
                    { path: "pages/:id", element: <LegacySiteRedirect /> },
                    // phase19.10 — модерация клиентских отзывов на товары.
                    { path: "reviews", element: <ProductReviewsPage /> },
                    // phase19.8.5 — ручные витринные отзывы («Свидетельства»; элемент секции «Отзывы» о магазине).
                    { path: "testimonials", element: <SiteTestimonialsPage /> },
                  ],
                },
                {
                  path: "customers",
                  // permission VIEW даёт доступ к списку (маскированные PII),
                  // MANAGE проверяется внутри страниц.
                  element: (
                    <RequirePermission permissions={["SHOP_VIEW_CUSTOMERS"]} />
                  ),
                  children: [
                    { index: true, element: <CustomersListPage /> },
                    { path: ":id", element: <CustomerDetailPage /> },
                  ],
                },
                // «Сообщения» — окно с кнопкой в углу (SC64): старый адрес ведёт на главную с открытым окном.
                { path: "messages", element: <MessagesRedirect /> },
                {
                  path: "wms",
                  element: <RequirePermission permissions={["SHOP_WMS_VIEW"]} />,
                  children: [
                    { index: true, element: <WmsDashboardPage /> },
                    { path: "stock", element: <WmsStockPage /> },
                    { path: "warehouses", element: <WmsWarehousesPage /> },
                    {
                      path: "receipts",
                      children: [
                        { index: true, element: <WmsReceiptsListPage /> },
                        { path: "new", element: <WmsReceiptEditPage /> },
                        { path: ":id", element: <WmsReceiptEditPage /> },
                      ],
                    },
                    {
                      path: "writeoffs",
                      children: [
                        { index: true, element: <WmsWriteoffsPage /> },
                        { path: "new", element: <WmsWriteoffEditPage /> },
                        { path: ":id", element: <WmsWriteoffEditPage /> },
                      ],
                    },
                    {
                      // phase19.14 — очередь заданий и экран одного задания.
                      path: "picking",
                      children: [
                        { index: true, element: <WmsPickingPage /> },
                        { path: ":id", element: <WmsPickingTaskPage /> },
                      ],
                    },
                    {
                      // phase19.23 этап B — документ «Перемещение» (MV-…).
                      path: "transfers",
                      children: [
                        { index: true, element: <WmsTransfersPage /> },
                        { path: ":id", element: <WmsTransferEditPage /> },
                      ],
                    },
                    { path: "bins", element: <WmsBinsPage /> },
                    { path: "expiring", element: <WmsExpiringPage /> },
                    { path: "channel-reserves", element: <WmsChannelReservesPage /> },
                    { path: "settings", element: <WmsSettingsPage /> },
                  ],
                },
                // phase19.31 (SC15) — «Настройки»: разделы под общим каркасом
                // «список слева — раздел справа» (router-settings-hub.tsx).
                settingsHubRoute,
                // phase19.16 Р1 — «Сайт → Вид сайта» (router-site-view.tsx).
                siteViewRoute,
                // phase19.16 Р3в — «Сайт → SEO» (SB145), вне каркаса вида сайта.
                siteSeoRoute,
                {
                  // phase19 — журнал уведомлений + инвайты в ЛК (зона «Клиенты»).
                  path: "settings/notifications",
                  element: <RequirePermission permissions={["SHOP_MANAGE_SETTINGS"]} />,
                  children: [{ index: true, element: <NotificationLogPage /> }],
                },
                {
                  path: "orders",
                  element: <RequirePermission permissions={["SHOP_VIEW_ORDERS"]} />,
                  children: [
                    { index: true, element: <OrdersListPage /> },
                    // Объединённый Канбан заказов и возвратов: тип
                    // переключается через ?type=orders|returns (default —
                    // orders). См. OrdersBoardPage.
                    { path: "board", element: <OrdersBoardPage /> },
                    // phase19.13 шаг 5 — «Требуют внимания» по отправлениям.
                    { path: "attention", element: <OrdersAttentionPage /> },
                    { path: "new", element: <OrderCreatePage /> },
                    // Стадии заказов и возвратов — разделы «Настроек»
                    // (router-settings-hub.tsx), адреса прежние.
                    // `returns/board` оставлен для обратной совместимости —
                    // редиректит на объединённый канбан с type=returns.
                    {
                      path: "returns/board",
                      element: <Navigate to="../board?type=returns" replace />,
                    },
                    { path: ":id", element: <OrderDetailPageOrders /> },
                  ],
                },
                {
                  // phase10.5.Y — Carrier Management. SHOP_VIEW_ORDERS — листать,
                  // SHOP_MANAGE_CARRIERS — настраивать (проверяется на бэкенде).
                  path: "carriers",
                  element: <RequirePermission permissions={["SHOP_VIEW_ORDERS"]} />,
                  children: [
                    { index: true, element: <ShopCarriersPage /> },
                    { path: "pickup-points", element: <ShopPickupPointsPage /> },
                    // phase19.12 — настройки ТК переехали из модалки на страницу.
                    { path: ":carrier/settings", element: <ShopCarrierSettingsPage /> },
                  ],
                },
                {
                  // phase14.0 — Avito Cabinet (синхра каталога/заказов/отзывов с Avito API).
                  path: "avito",
                  element: <RequirePermission permissions={["SHOP_AVITO_CABINET_VIEW"]} />,
                  children: [
                    {
                      element: <AvitoCabinetLayout />,
                      children: [
                        { index: true, element: <AvitoDashboardPage /> },
                        { path: "listings", element: <AvitoListingsPage /> },
                        { path: "listings/:productId", element: <AvitoListingDetailPage /> },
                        { path: "orders", element: <AvitoOrdersPage /> },
                        { path: "reviews", element: <AvitoReviewsPage /> },
                        { path: "sync-log", element: <AvitoSyncLogPage /> },
                      ],
                    },
                    // phase14.0.4 — Отдельный раздел «Финансы Avito» (без шапки Avito-кабинета).
                    {
                      path: "finance",
                      element: (
                        <RequirePermission permissions={["SHOP_AVITO_FINANCE_VIEW"]}>
                          <AvitoFinancePage />
                        </RequirePermission>
                      ),
                    },
                  ],
                },
                {
                  path: "crm",
                  element: <RequirePermission permissions={["SHOP_USE_CRM"]} />,
                  children: [
                    { index: true, element: <KanbanBoardPage /> },
                    { path: "board", element: <KanbanBoardPage /> },
                    { path: "deals", element: <DealsListPage /> },
                    { path: "deals/:id", element: <DealDetailPage /> },
                    { path: "activities", element: <ActivitiesPage /> },
                    {
                      path: "documents",
                      element: <RequirePermission permissions={["SHOP_VIEW_DOCUMENTS"]} />,
                      children: [
                        { index: true, element: <DocumentsListPage /> },
                        { path: ":id", element: <DocumentDetailPage /> },
                      ],
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
    { path: "*", element: <NotFound /> },
  ],
  // basename берётся из vite base (см. vite.config.ts):
  //   dev (pnpm dev) → "/admin/"  → trim trailing slash → "/admin"
  //   prod (pnpm build) → "/"
  // BASE_URL Vite автоматически выставляет в import.meta.env.
  { basename: import.meta.env.BASE_URL.replace(/\/$/, "") || "/" },
);
